// Lightweight ring buffer that captures the last N console errors and
// unhandled exceptions. Used by the support ticket form so users don't
// have to re-explain "what just happened" when filing a bug.

export interface CapturedError {
  ts: number;
  source: "console.error" | "window.error" | "unhandledrejection";
  message: string;
  stack?: string;
  url?: string;
}

const MAX_ENTRIES = 50;
const buffer: CapturedError[] = [];
let installed = false;

function push(entry: CapturedError) {
  buffer.push(entry);
  if (buffer.length > MAX_ENTRIES) buffer.shift();
}

function safeStringify(value: unknown): string {
  try {
    if (typeof value === "string") return value;
    if (value instanceof Error) return value.message;
    return JSON.stringify(value, (_k, v) =>
      typeof v === "function" ? "[fn]" : v
    );
  } catch {
    return String(value);
  }
}

export function installConsoleBuffer() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const originalError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    try {
      push({
        ts: Date.now(),
        source: "console.error",
        message: args.map(safeStringify).join(" ").slice(0, 1000),
        url: window.location.href,
      });
    } catch {
      /* swallow */
    }
    originalError(...args);
  };

  window.addEventListener("error", (e) => {
    push({
      ts: Date.now(),
      source: "window.error",
      message: (e.message || "Uncaught error").slice(0, 1000),
      stack: e.error?.stack?.slice(0, 2000),
      url: window.location.href,
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason: any = e.reason;
    push({
      ts: Date.now(),
      source: "unhandledrejection",
      message: safeStringify(reason?.message || reason).slice(0, 1000),
      stack: reason?.stack?.slice(0, 2000),
      url: window.location.href,
    });
  });
}

export function getRecentErrors(): CapturedError[] {
  return [...buffer];
}

export function clearRecentErrors() {
  buffer.length = 0;
}
